/**
 * useCallCentreLive: React hook over `CallCentreLiveClient`.
 *
 * - Connects to `{config.dashboardsUrl}/hubs/dashboard/callcentre` with `config.getToken` (or the overrides below),
 *   subscribes and keeps the latest `badges`, overall `board`, followed campaign's board and `online` in state.
 * - Never blocks or throws: when the hub is down `isConnected` stays false and the values stay null, so the page keeps
 *   its REST data. It retries quietly in the background (5 s, 15 s, 30 s, 60 s, then every 2 minutes), and the client
 *   resyncs itself after an automatic reconnect (`onResync` fires: a good moment to refetch the REST baseline).
 * - `campaignId` follows one campaign's board (console staff only; `campaignBoard`).
 */
import { useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { useCCMS } from '../../../react/hooks/useCCMS';
import { ConnectionState } from '../../shared/types';
import { CallCentreLiveClient } from '../CallCentreLiveClient';
const RETRY_DELAYS_MS = [5000, 15000, 30000, 60000, 120000];
export function useCallCentreLive(options = {}) {
    const { config } = useCCMS();
    const { enabled = true, campaignId = null } = options;
    const url = options.url ?? config.dashboardsUrl ?? '';
    const [isConnected, setIsConnected] = useState(false);
    const [isAdmin, setIsAdmin] = useState(null);
    const [badges, setBadges] = useState(null);
    const [board, setBoard] = useState(null);
    const [campaignBoard, setCampaignBoard] = useState(null);
    const [online, setOnline] = useState(null);
    const [error, setError] = useState(null);
    const clientRef = useRef(null);
    const campaignRef = useRef(campaignId);
    const handlersRef = useRef(options);
    handlersRef.current = options;
    const tokenRef = useRef(options.getToken ?? config.getToken);
    tokenRef.current = options.getToken ?? config.getToken;
    // One client per (enabled, url). Quiet retries; never throws into the page.
    useEffect(() => {
        if (!enabled || !url)
            return undefined;
        let disposed = false;
        let attempt = 0;
        let timer = null;
        const client = new CallCentreLiveClient(url, async () => {
            try {
                const get = tokenRef.current;
                return get ? await get() : null;
            }
            catch {
                return null;
            }
        }, { logLevel: signalR.LogLevel.None });
        clientRef.current = client;
        client.on('subscribed', (m) => setIsAdmin(!!m.isAdmin));
        client.on('badges', (m) => {
            setBadges(m.badges);
            handlersRef.current.onBadges?.(m);
        });
        client.on('board', (m) => {
            if (m.board.campaignID == null)
                setBoard(m.board);
            else if (m.board.campaignID === campaignRef.current)
                setCampaignBoard(m.board);
            handlersRef.current.onBoard?.(m);
        });
        client.on('online', (m) => {
            setOnline(m.online);
            handlersRef.current.onOnline?.(m);
        });
        client.on('resynced', () => handlersRef.current.onResync?.());
        const schedule = () => {
            if (disposed || timer)
                return;
            const delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
            attempt += 1;
            timer = setTimeout(() => {
                timer = null;
                void start();
            }, delay);
        };
        const start = async () => {
            if (disposed)
                return;
            try {
                await client.connect();
                await client.subscribe();
                if (campaignRef.current != null) {
                    const b = await client.joinCampaign(campaignRef.current);
                    if (!disposed && b)
                        setCampaignBoard(b);
                }
                attempt = 0;
                setError(null);
            }
            catch (err) {
                if (disposed)
                    return;
                setError(err instanceof Error ? err : new Error(String(err)));
                schedule();
            }
        };
        client.onStateChange((state) => {
            if (disposed)
                return;
            setIsConnected(state === ConnectionState.Connected);
            // The automatic reconnect gave up (or the server closed the connection): start again quietly.
            if (state === ConnectionState.Disconnected)
                schedule();
        });
        void start();
        return () => {
            disposed = true;
            if (timer)
                clearTimeout(timer);
            clientRef.current = null;
            setIsConnected(false);
            void client.destroy().catch(() => undefined);
        };
    }, [enabled, url]);
    // Follow another campaign's board: leave the previous one, join the new one. While disconnected, `start` (and the
    // client's own resync after a reconnect) joins whatever campaignRef holds, so nothing else is needed here.
    useEffect(() => {
        const previous = campaignRef.current;
        if (previous === campaignId)
            return;
        campaignRef.current = campaignId;
        setCampaignBoard(null);
        const client = clientRef.current;
        if (!client)
            return;
        void (async () => {
            try {
                if (previous != null)
                    await client.leaveCampaign(previous);
                if (campaignId != null && client.isConnected()) {
                    const b = await client.joinCampaign(campaignId);
                    if (campaignRef.current === campaignId && b)
                        setCampaignBoard(b);
                }
            }
            catch {
                // Quiet: the page keeps its REST data and the next reconnect re-joins.
            }
        })();
    }, [campaignId]);
    return { isConnected, isAdmin, badges, board, campaignBoard, online, error };
}
//# sourceMappingURL=useCallCentreLive.js.map